import React, { useCallback, useEffect, useState } from 'react';
import PageHeader from './ui/PageHeader';
import Button from './ui/Button';
import ConfirmDialog from './ui/ConfirmDialog';
import { useToast } from '../contexts/ToastContext';
import { AdminQueueState } from './ui/AdminQueueState';
import { useAdminTabParam } from '../hooks/useAdminTabParam';
import apiService from '../services/ApiService';
import type { PaidFeatureFlags } from '../types/event';

const PAYMENT_TABS = [
  'summary',
  'accounts',
  'orders',
  'disputes',
  'webhooks',
  'reconcile',
  'flags',
] as const;
type Section = (typeof PAYMENT_TABS)[number];

interface SummaryData {
  stripe_mode: string;
  stripe_configured: boolean;
  flags: PaidFeatureFlags;
  counts: Record<string, number>;
}

const AdminPayments: React.FC = () => {
  const { showToast } = useToast();
  const [section, setSection] = useAdminTabParam(PAYMENT_TABS, 'summary');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<SummaryData | null>(null);
  const [flags, setFlags] = useState<PaidFeatureFlags>({});
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [issues, setIssues] = useState<Record<string, unknown>[]>([]);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [repairReason, setRepairReason] = useState('');
  const [refundBusyId, setRefundBusyId] = useState<number | null>(null);
  const [refundTargetId, setRefundTargetId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (section === 'summary' || section === 'flags') {
        const data = (await apiService.getAdminPaymentOps('summary')) as unknown as SummaryData;
        setSummary(data);
        setFlags(data.flags || {});
        setRows([]);
        setIssues([]);
      } else if (section === 'reconcile') {
        const data = await apiService.getPaymentReconcile();
        setIssues(data.issues || []);
        setRows([]);
      } else {
        const params: Record<string, string> = { page: '1', per_page: '25' };
        if (q) params.q = q;
        if (status) params.status = status;
        const [data, summaryData] = await Promise.all([
          apiService.getAdminPaymentOps(section, params),
          section === 'orders'
            ? (apiService.getAdminPaymentOps('summary') as Promise<unknown>).then(
                (d) => d as SummaryData
              )
            : Promise.resolve(null),
        ]);
        setRows((data.rows as Record<string, unknown>[]) || []);
        setIssues([]);
        if (summaryData?.flags) setFlags(summaryData.flags);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load payments admin';
      setError(message);
      showToast(message, 'error');
    } finally {
      setLoading(false);
    }
  }, [section, q, status, showToast]);

  useEffect(() => {
    void load();
  }, [load]);

  const confirmRefundOrder = async () => {
    if (refundTargetId == null) return;
    const orderId = refundTargetId;
    try {
      setRefundBusyId(orderId);
      await apiService.refundOrder(orderId, 'requested_by_admin');
      showToast('Refund requested — wait for webhook to update registration/ticket state', 'success');
      setRefundTargetId(null);
      await load();
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Refund failed', 'error');
    } finally {
      setRefundBusyId(null);
    }
  };

  const runRepair = async (issue: Record<string, unknown>) => {
    const repair = String(issue.safe_repair || '');
    if (!repair) {
      showToast('No safe repair for this issue', 'error');
      return;
    }
    if (!repairReason.trim()) {
      showToast('Reason is required for repairs', 'error');
      return;
    }
    try {
      await apiService.repairPaymentIssue({
        repair,
        reason: repairReason.trim(),
        registration_id: issue.registration_id != null ? Number(issue.registration_id) : undefined,
        order_id: issue.order_id != null ? Number(issue.order_id) : undefined,
        event_id: issue.event_id != null ? Number(issue.event_id) : undefined,
      });
      showToast('Repair applied', 'success');
      setRepairReason('');
      await load();
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Repair failed', 'error');
    }
  };

  const tabs: { key: Section; label: string }[] = [
    { key: 'summary', label: 'Summary' },
    { key: 'accounts', label: 'Accounts' },
    { key: 'orders', label: 'Orders' },
    { key: 'disputes', label: 'Disputes' },
    { key: 'webhooks', label: 'Webhooks' },
    { key: 'reconcile', label: 'Reconcile' },
    { key: 'flags', label: 'Flags' },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Payments"
        description="Stripe Connect, orders, refunds, disputes, and feature flags. Super-admin only. No arbitrary mark-paid."
      />

      <div className="flex flex-wrap gap-2" role="tablist" aria-label="Payments sections">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={section === tab.key}
            className={`btn-ghost btn-sm min-h-[44px] ${section === tab.key ? 'text-orange-400 border-orange-500/40' : ''}`}
            onClick={() => setSection(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {(section === 'accounts' || section === 'orders' || section === 'webhooks') && (
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label htmlFor="payments-q" className="input-label">
              Search
            </label>
            <input
              id="payments-q"
              className="input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="ID or reference"
            />
          </div>
          {(section === 'orders' || section === 'webhooks') && (
            <div>
              <label htmlFor="payments-status" className="input-label">
                Status
              </label>
              <input
                id="payments-status"
                className="input"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                placeholder="paid, failed…"
              />
            </div>
          )}
          <Button type="button" variant="secondary" size="sm" onClick={() => void load()}>
            Apply
          </Button>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-gray-400" role="status">
          Loading payments data…
        </p>
      ) : error ? (
        <AdminQueueState variant="error" title="Could not load payments" description={error} onRetry={() => void load()} />
      ) : section === 'summary' && summary ? (
        <div className="space-y-4">
          <p className="text-sm text-gray-400">
            Mode: <span className="text-white">{summary.stripe_mode}</span>
            {' · '}
            Configured: <span className="text-white">{summary.stripe_configured ? 'yes' : 'no'}</span>
          </p>
          <p className="text-xs text-gray-500">
            Email delivery is unavailable on current hosting. Notifications are in-app only.
          </p>
          <dl className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {Object.entries(summary.counts || {}).map(([key, value]) => (
              <div key={key} className="form-section !p-4">
                <dt className="text-xs text-gray-500">{key.replace(/_/g, ' ')}</dt>
                <dd className="text-xl text-white mt-1">{value}</dd>
              </div>
            ))}
          </dl>
        </div>
      ) : section === 'flags' ? (
        <div className="space-y-3">
          <p className="text-sm text-gray-400">
            Paid event capabilities are shipped final features (always on). Emergency off: set{' '}
            <code className="text-gray-300">PAID_FLAG_*</code> to 0 on the server.
          </p>
          <ul className="space-y-3">
            {Object.entries(flags || {}).map(([key, enabledValue]) => {
              const enabled = Boolean(enabledValue);
              return (
                <li
                  key={key}
                  className="form-section flex flex-wrap items-center justify-between gap-3 !py-3"
                >
                  <div>
                    <p className="text-sm text-white font-medium">{key}</p>
                    <p className="text-xs text-gray-500">
                      {enabled ? 'On · shipped' : 'Off (env emergency override)'}
                    </p>
                  </div>
                  <span
                    className={`text-sm ${enabled ? 'text-emerald-400' : 'text-gray-500'}`}
                    aria-label={`${key}: ${enabled ? 'on' : 'off'}`}
                  >
                    {enabled ? 'On' : 'Off'}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      ) : section === 'reconcile' ? (
        <div className="space-y-4">
          <div>
            <label htmlFor="repair-reason" className="input-label">
              Repair reason (required)
            </label>
            <input
              id="repair-reason"
              className="input"
              value={repairReason}
              onChange={(e) => setRepairReason(e.target.value)}
              placeholder="Why this repair is needed"
            />
          </div>
          {issues.length === 0 ? (
            <AdminQueueState variant="empty" title="No reconciliation issues" description="Orders and registrations look consistent." />
          ) : (
            <ul className="space-y-3">
              {issues.map((issue, idx) => (
                <li key={idx} className="form-section !p-4">
                  <p className="text-sm text-white font-medium">{String(issue.code)}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    severity {String(issue.severity)} · order {String(issue.order_id ?? '—')} · reg{' '}
                    {String(issue.registration_id ?? '—')}
                  </p>
                  {issue.safe_repair ? (
                    <Button
                      type="button"
                      size="sm"
                      className="mt-3"
                      variant="secondary"
                      onClick={() => void runRepair(issue)}
                    >
                      Apply {String(issue.safe_repair)}
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : rows.length === 0 ? (
        <AdminQueueState variant="empty" title="No rows" description="Nothing matched this filter." />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-white/[0.06]">
          <table className="min-w-full text-sm text-left">
            <thead className="bg-[#0c0c0c] text-gray-400">
              <tr>
                {Object.keys(rows[0])
                  .slice(0, 8)
                  .map((col) => (
                    <th key={col} className="px-3 py-2 font-medium whitespace-nowrap">
                      {col}
                    </th>
                  ))}
                {section === 'orders' ? (
                  <th className="px-3 py-2 font-medium whitespace-nowrap">actions</th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => {
                const orderId = Number(row.id);
                const canRefund =
                  section === 'orders' &&
                  String(row.status || '') === 'paid' &&
                  Number.isFinite(orderId) &&
                  orderId > 0 &&
                  Boolean(flags.payment_refunds_enabled);
                return (
                <tr key={idx} className="border-t border-white/[0.06]">
                  {Object.keys(rows[0])
                    .slice(0, 8)
                    .map((col) => (
                      <td key={col} className="px-3 py-2 text-gray-200 whitespace-nowrap max-w-[220px] truncate">
                        {row[col] === null || row[col] === undefined ? '—' : String(row[col])}
                      </td>
                    ))}
                  {section === 'orders' ? (
                    <td className="px-3 py-2">
                      {canRefund ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="danger"
                          loading={refundBusyId === orderId}
                          onClick={() => setRefundTargetId(orderId)}
                        >
                          Refund
                        </Button>
                      ) : (
                        <span className="text-xs text-gray-600">—</span>
                      )}
                    </td>
                  ) : null}
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        isOpen={refundTargetId != null}
        title="Issue full refund?"
        description={
          refundTargetId != null ? (
            <>
              Refund order <strong className="text-white">#{refundTargetId}</strong> in full. Stripe
              webhooks will update registration/ticket state. This cannot be undone from this screen.
            </>
          ) : undefined
        }
        confirmLabel="Refund order"
        cancelLabel="Cancel"
        destructive
        loading={refundBusyId != null}
        onCancel={() => {
          if (refundBusyId == null) setRefundTargetId(null);
        }}
        onConfirm={() => void confirmRefundOrder()}
      />
    </div>
  );
};

export default AdminPayments;
